import {
    createElement,
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react'
import {
    type Appearance,
    type ResolvedTheme,
    applyResolvedTheme,
    getStoredAppearance,
    persistAppearance,
    resolveTheme,
} from '@/lib/appearance'

export type { Appearance }

interface AppearanceContextValue {
    appearance: Appearance
    resolvedTheme: ResolvedTheme
    setAppearance: (value: Appearance) => void
    toggleTheme: () => void
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null)

export function AppearanceProvider({ children }: { children: ReactNode }) {
    const [appearance, setAppearanceState] = useState<Appearance>(() => {
        const stored = getStoredAppearance()
        applyResolvedTheme(resolveTheme(stored))
        return stored
    })
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
        resolveTheme(getStoredAppearance()),
    )

    const setAppearance = useCallback((value: Appearance) => {
        persistAppearance(value)
        const resolved = resolveTheme(value)
        applyResolvedTheme(resolved)
        setAppearanceState(value)
        setResolvedTheme(resolved)
    }, [])

    useLayoutEffect(() => {
        persistAppearance(appearance)

        if (appearance === 'system') {
            const media = window.matchMedia('(prefers-color-scheme: dark)')
            const updateTheme = () => {
                const next = media.matches ? 'dark' : 'light'
                applyResolvedTheme(next)
                setResolvedTheme(next)
            }

            updateTheme()
            media.addEventListener('change', updateTheme)

            return () => media.removeEventListener('change', updateTheme)
        }

        applyResolvedTheme(appearance)
        setResolvedTheme(appearance)

        return undefined
    }, [appearance])

    const value = useMemo<AppearanceContextValue>(
        () => ({
            appearance,
            resolvedTheme,
            setAppearance,
            toggleTheme: () => {
                setAppearance(resolveTheme(appearance) === 'dark' ? 'light' : 'dark')
            },
        }),
        [appearance, resolvedTheme, setAppearance],
    )

    return createElement(AppearanceContext.Provider, { value }, children)
}

export function useAppearance() {
    const context = useContext(AppearanceContext)

    if (!context) {
        throw new Error('useAppearance must be used within an AppearanceProvider')
    }

    return context
}
